import { HttpClient } from 'api/http-client';
import { ProjectId, DocumentId } from 'api/models';
import { Task, TaskSourceType } from 'api/tasks';
import { getDocumentName } from 'components/organisms/ck-collab-editor/get-document-name';
import { isCKWsCloud } from 'config/environment';
import { DocumentResourcePayload, EditorModes } from 'data/documents/constants';
import { logger } from 'services';
import {
  updateTaskAssigneeAndResponsibleCompany,
  updateTasksAssigneeAndResponsibleCompany,
} from 'utils/strategic-partner-utils';
import {
  DocumentContainer,
  Document,
  EditorCollaborateSessionExists,
  SingleCollaborateSessionExists,
  CollaborationEncodededResponse,
  DocumentSkelton,
  DocumentVersion,
  AddVersionPayload,
} from './types/document';

const DOCUMENTS_API = isCKWsCloud() ? '/cloud-collaborate' : '/collaborate';

class DocumentsHttpClient extends HttpClient {
  public async getDocumentsList(projectID: number): Promise<DocumentSkelton[]> {
    return this.get<DocumentSkelton[]>(`/projects/${projectID}/documents`);
  }

  public async getDocument(
    projectID: number,
    documentID: number,
  ): Promise<DocumentContainer> {
    return this.get<DocumentContainer>(
      `/projects/${projectID}/documents/${documentID}`,
    );
  }

  // three endpoints here are meant for document versioning without real-time collaboration. To be integrated with revisionsAdapter if we implement later
  public async getAllDocumentVersions(
    documentID: number,
    mode: EditorModes,
  ): Promise<DocumentVersion[]> {
    return this.get<DocumentVersion[]>(
      `/documents/${documentID}/get-versions/${mode}`,
    );
  }

  public async addDocumentVersion(
    documentID: number,
    payload: AddVersionPayload[],
  ): Promise<DocumentVersion[]> {
    return this.post<DocumentVersion[], AddVersionPayload[]>(
      `/documents/${documentID}/add-version`,
      payload,
    );
  }

  public async getDocumentVersion(
    documentID: number,
    versionId: string,
  ): Promise<DocumentVersion[]> {
    return this.get<DocumentVersion[]>(
      `/documents/${documentID}/version/${versionId}`,
    );
  }

  public async getTasks(
    projectID: number,
    documentID: number,
  ): Promise<Task[]> {
    return this.get<Task[]>(
      `/projects/${projectID}/documents/${documentID}/tasks`,
    ).then((tasks) => updateTasksAssigneeAndResponsibleCompany(tasks));
  }

  public async createTask(
    projectID: number,
    documentID: number,
    data: Partial<Task>,
    meta: {
      resourceSectionId?: number;
      docRefText?: string;
    },
  ): Promise<Task> {
    const initSource = {
      sourceType: TaskSourceType.Document,
      sourceId: documentID,
    };

    let finalSource;

    if (meta.resourceSectionId) {
      finalSource = {
        ...initSource,
        metaData: {
          resourceSectionId: meta.resourceSectionId,
        },
      };
    } else if (meta.docRefText) {
      finalSource = {
        ...initSource,
        metaData: {
          taskFromDoc: meta.docRefText,
        },
      };
    } else {
      finalSource = initSource;
    }

    const payload = {
      ...data,
      source: finalSource,
    };

    return this.post(`/projects/${projectID}/tasks`, payload).then((task) =>
      updateTaskAssigneeAndResponsibleCompany(task as Task),
    );
  }

  public async createSectionTask(
    projectID: number,
    data: Partial<Task>,
    meta: {
      resourceSectionId?: number;
      documentSectionId: number;
      private: boolean;
    },
  ): Promise<Task> {
    const source = {
      sourceType: TaskSourceType.DocumentSection,
      sourceId: meta.documentSectionId,
      metaData: {
        resourceSectionId: meta.resourceSectionId,
      },
    };

    const payload = {
      ...data,
      private: meta.private,
      source,
    };

    return this.post(`/projects/${projectID}/tasks`, payload).then((task) =>
      updateTaskAssigneeAndResponsibleCompany(task as Task),
    );
  }

  public async addExistingSectionTask(
    projectID: number,
    taskList: Task[],
    meta: {
      resourceSectionId?: number;
      documentSectionId: number;
      private: boolean;
    },
  ): Promise<Task[]> {
    const source = {
      sourceType: TaskSourceType.DocumentSection,
      sourceId: meta.documentSectionId,
      metaData: {
        resourceSectionId: meta.resourceSectionId,
      },
    };

    const tasksWithSection = taskList.map((task) => ({
      taskName: task.taskName,
      taskId: task.taskId,
      source,
    }));

    return this.post(`/projects/${projectID}/add-sources`, tasksWithSection);
  }

  public async deleteExistingSectionTask(
    projectID: number,
    task: Partial<Task>,
  ): Promise<Task> {
    return this.put(`/projects/${projectID}/delete-sources`, task);
  }

  public async updateDocument(
    projectID: number,
    documentID: number,
    sectionid: string,
    versionId: string,
    data,
  ): Promise<DocumentContainer> {
    const payload = {
      versionId,
      document: {
        sections: [
          {
            documentSectionId: sectionid,
            content: data,
          },
        ],
      },
    };

    return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
  }

  public async updateStatusUpdateDocument(
    statusUpdateId: number,
    documentID: number,
    sectionid: string,
    versionId: string,
    data,
  ): Promise<DocumentContainer> {
    const payload = {
      versionId,
      document: {
        sections: [
          {
            documentSectionId: sectionid,
            content: data,
          },
        ],
      },
    };

    return this.put(
      `/status-updates/${statusUpdateId}/document/${documentID}`,
      payload,
    );
  }

  public async createPrivateSection(
    projectID: number,
    documentID: number,
    data,
  ): Promise<Document> {
    const payload = {
      document: {
        sections: [
          {
            meta: {
              sectionType: EditorModes.Private,
            },
            content: data,
          },
        ],
      },
    };

    return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
  }

  public async createStatusUpdatePrivateSection(
    statusUpdateId: number,
    documentID: number,
    data,
  ): Promise<Document> {
    const payload = {
      document: {
        sections: [
          {
            meta: {
              sectionType: EditorModes.Private,
            },
            content: data,
          },
        ],
      },
    };

    return this.put(
      `/status-updates/${statusUpdateId}/document/${documentID}`,
      payload,
    );
  }

  public async updateDocumentTaskSectionsMeta(
    projectID: number,
    documentID: number,
    section: {
      id: string;
      type: string;
    },
    map: { resourceSectionId: number; resourceSectionName: string }[],
  ): Promise<DocumentContainer> {
    // TODO: Now the content is a mandatory field. That
    // is not optimal. Remove that rule
    const payload = {
      document: {
        sections: [
          {
            documentSectionId: section.id,
            meta: {
              sectionType: section.type,
              resourceSections: map,
            },
          },
        ],
      },
    };

    return this.put(`/projects/${projectID}/documents/${documentID}`, payload);
  }

  public async updateDocumentTitle(
    projectID: number,
    documentID: number,
    title,
  ): Promise<DocumentContainer> {
    const payload = {
      document: {
        title,
      },
    };

    return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
  }

  public async updateDocumentFreeze(
    projectID: number,
    documentID: number,
    frozen,
  ): Promise<Document> {
    const payload = {
      document: {
        frozen,
      },
    };

    return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
  }

  public async updateDocumentResources({
    projectId,
    documentId,
    documentSectionId,
    sectionType,
    resourceSections,
  }: DocumentResourcePayload): Promise<DocumentContainer> {
    const payload = {
      document: {
        sections: [
          {
            documentSectionId,
            meta: {
              sectionType,
              resourceSections,
            },
          },
        ],
      },
    };

    return this.put(`/projects/${projectId}/documents/${documentId}/`, payload);
  }

  public flushCollaborationSession = async (
    projectId: ProjectId,
    docId: DocumentId,
  ) => {
    const privateDocName = getDocumentName(docId, EditorModes.Private);
    const publicDocName = getDocumentName(docId, EditorModes.Public);
    const privateDocument = this.delete(
      `/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${privateDocName}`,
    );
    const publicDocument = this.delete(
      `/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${publicDocName}`,
    );

    const result = await Promise.allSettled([privateDocument, publicDocument]);

    result
      .filter((res) => res.status === 'rejected')
      .forEach((res) =>
        logger.log(
          `couldn't delete collab document and reason ${JSON.stringify(res)}`,
        ),
      );
  };

  public getCollaborationSession = async (
    projectId: ProjectId,
    docId: DocumentId,
  ): Promise<EditorCollaborateSessionExists> => {
    const privateDocName = getDocumentName(docId, EditorModes.Private);
    const publicDocName = getDocumentName(docId, EditorModes.Public);

    const publicDocSession = await this.get<
      null,
      SingleCollaborateSessionExists
    >(
      `/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${publicDocName}`,
    );

    const privateDocSession = await this.get<
      null,
      SingleCollaborateSessionExists
    >(
      `/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${privateDocName}`,
    );

    return {
      publicDocSession,
      privateDocSession,
    };
  };

  public getCollaborationToken = async (clientRegion?: string) => {
    const params = clientRegion ? { clientRegion } : undefined;

    return this.get<CollaborationEncodededResponse>(DOCUMENTS_API, {
      params,
    })
      .then((response) => {
        logger.debug(
          `collaborate token api response: ${
            response?.length > 0 ? 'token available' : 'token not available'
          }`,
        );

        return response;
      })
      .catch((error) => {
        logger.error('get collaborate token api failed', error);
        throw error;
      });
  };
}

export const DocumentsAPI = new DocumentsHttpClient();
